import { useState } from 'react';
import {
  FileText, MessageSquare, Phone, Mail, Users, Calendar,
  Search, Zap, ChevronRight, Lightbulb, Mic, Globe, Building
} from 'lucide-react';

const CLONE_CATEGORIES = [
  {
    id: 'docs',
    icon: FileText,
    label: 'Documents & Factures',
    color: 'text-amber-600 bg-amber-50 border-amber-200',
    dotColor: 'bg-amber-500',
    questions: [
      "Est-ce qu'on a reçu une facture récemment ?",
      "Trouve-moi le dernier devis envoyé",
      "Quel était le contenu du PDF reçu par email ?",
      "Résume les documents reçus cette semaine",
    ],
  },
  {
    id: 'whatsapp',
    icon: MessageSquare,
    label: 'WhatsApp',
    color: 'text-green-600 bg-green-50 border-green-200',
    dotColor: 'bg-green-500',
    questions: [
      "Qu'est-ce qu'on m'a envoyé sur WhatsApp ?",
      "Résume mes échanges WhatsApp de cette semaine",
      "Quel vocal j'ai reçu récemment ?",
      "Qui m'a contacté par WhatsApp ?",
    ],
  },
  {
    id: 'calls',
    icon: Phone,
    label: 'Appels & Réunions',
    color: 'text-blue-600 bg-blue-50 border-blue-200',
    dotColor: 'bg-blue-500',
    questions: [
      "De quoi on a parlé lors du dernier appel ?",
      "Résume ma dernière réunion Teams",
      "Quels engagements ont été pris au téléphone ?",
      "Quel client a appelé cette semaine ?",
    ],
  },
  {
    id: 'emails',
    icon: Mail,
    label: 'Emails',
    color: 'text-purple-600 bg-purple-50 border-purple-200',
    dotColor: 'bg-purple-500',
    questions: [
      "Quel était le dernier email reçu ?",
      "Trouve les emails concernant le projet X",
      "Qu'est-ce qui a été envoyé comme pièce jointe ?",
      "Résume les échanges email avec ce client",
    ],
  },
  {
    id: 'suivi',
    icon: Search,
    label: 'Suivi & Engagements',
    color: 'text-rose-600 bg-rose-50 border-rose-200',
    dotColor: 'bg-rose-500',
    questions: [
      "Y a-t-il des engagements en attente ?",
      "Quelles actions ont été promises cette semaine ?",
      "Quel est le statut du dossier en cours ?",
      "Qui attend une réponse de ma part ?",
    ],
  },
  {
    id: 'time',
    icon: Calendar,
    label: 'Recherche temporelle',
    color: 'text-cyan-600 bg-cyan-50 border-cyan-200',
    dotColor: 'bg-cyan-500',
    questions: [
      "Qu'est-ce qui s'est passé hier ?",
      "Résume mes communications de cette semaine",
      "Qu'est-ce qui a changé ce mois-ci ?",
      "Quels échanges ont eu lieu aujourd'hui ?",
    ],
  },
];

const GLOBAL_CATEGORIES = [
  {
    id: 'cross',
    icon: Users,
    label: 'Cross-collaborateur',
    color: 'text-indigo-600 bg-indigo-50 border-indigo-200',
    dotColor: 'bg-indigo-500',
    questions: [
      "Qui a parlé à ce client cette semaine ?",
      "Compare les échanges de Justine et Sardjan avec ce contact",
      "Quel collaborateur gère le dossier X ?",
      "Qui a le plus communiqué avec cette entreprise ?",
    ],
  },
  {
    id: 'docs',
    icon: FileText,
    label: 'Documents & Factures',
    color: 'text-amber-600 bg-amber-50 border-amber-200',
    dotColor: 'bg-amber-500',
    questions: [
      "Est-ce qu'un collaborateur a reçu une facture de X ?",
      "Trouve les devis envoyés cette semaine",
      "Quel document a été partagé par WhatsApp ?",
      "Résume les pièces jointes reçues par email",
    ],
  },
  {
    id: 'whatsapp',
    icon: MessageSquare,
    label: 'WhatsApp',
    color: 'text-green-600 bg-green-50 border-green-200',
    dotColor: 'bg-green-500',
    questions: [
      "Quels messages WhatsApp ont été échangés avec X ?",
      "Résume les vocaux reçus sur WhatsApp",
      "Qui a été contacté par WhatsApp aujourd'hui ?",
      "Qu'est-ce qui a été dit par WhatsApp cette semaine ?",
    ],
  },
  {
    id: 'calls',
    icon: Phone,
    label: 'Appels & Réunions',
    color: 'text-blue-600 bg-blue-50 border-blue-200',
    dotColor: 'bg-blue-500',
    questions: [
      "Quels appels ont eu lieu hier ?",
      "De quoi a parlé Justine avec le client X ?",
      "Résume les réunions Teams de cette semaine",
      "Quel collaborateur a passé le plus d'appels ?",
    ],
  },
  {
    id: 'global',
    icon: Globe,
    label: 'Vue d\'ensemble',
    color: 'text-teal-600 bg-teal-50 border-teal-200',
    dotColor: 'bg-teal-500',
    questions: [
      "Résume toutes les communications de cette semaine",
      "Quels sont les sujets les plus discutés ?",
      "Y a-t-il des urgences en cours ?",
      "Fais le point sur les échanges d'aujourd'hui",
    ],
  },
  {
    id: 'time',
    icon: Calendar,
    label: 'Recherche temporelle',
    color: 'text-cyan-600 bg-cyan-50 border-cyan-200',
    dotColor: 'bg-cyan-500',
    questions: [
      "Qu'est-ce qui s'est passé hier sur tous les canaux ?",
      "Résume les échanges de cette semaine par collaborateur",
      "Quoi de neuf aujourd'hui ?",
      "Compare les activités de la semaine dernière",
    ],
  },
];

export function ChatLexicon({ type = 'clone', onSelect, personName = '' }) {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const categories = type === 'global' ? GLOBAL_CATEGORIES : CLONE_CATEGORIES;

  const handleSelect = (question) => {
    const q = question.replace('ce client', personName || 'ce client');
    onSelect(q);
  };

  return (
    <div className="w-full max-w-3xl mx-auto" data-testid="chat-lexicon">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 mb-3">
          <Lightbulb className="w-7 h-7 text-primary" />
        </div>
        <h3 className="text-lg font-semibold tracking-tight">
          {type === 'global' ? 'Explorez vos données entreprise' : `Interrogez le clone de ${personName || 'votre collaborateur'}`}
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
          {type === 'global'
            ? 'Recherchez dans les emails, appels, WhatsApp et documents de tous vos collaborateurs'
            : 'Emails, appels, WhatsApp, vocaux, documents — tout est accessible'}
        </p>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5" data-testid="lexicon-categories">
        {categories.map((cat) => {
          const Icon = cat.icon;
          const isExpanded = expandedCategory === cat.id;

          return (
            <div key={cat.id} className="relative">
              <button
                onClick={() => setExpandedCategory(isExpanded ? null : cat.id)}
                className={`w-full text-left p-3 rounded-xl border transition-all duration-200 hover:shadow-sm ${
                  isExpanded
                    ? `${cat.color} border-current/20 shadow-sm`
                    : 'bg-card border-border hover:border-primary/30'
                }`}
                data-testid={`lexicon-cat-${cat.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${isExpanded ? '' : 'text-muted-foreground'}`} />
                  <span className="text-xs font-semibold tracking-tight">{cat.label}</span>
                </div>
                <p className="text-[11px] text-muted-foreground line-clamp-1">
                  {cat.questions[0]}...
                </p>
              </button>
            </div>
          );
        })}
      </div>

      {/* Expanded Questions */}
      {expandedCategory && (
        <div className="mt-3 p-4 rounded-xl border bg-card/50 backdrop-blur-sm animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid="lexicon-questions">
          {(() => {
            const cat = categories.find(c => c.id === expandedCategory);
            if (!cat) return null;
            const Icon = cat.icon;
            return (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-2 h-2 rounded-full ${cat.dotColor}`} />
                  <span className="text-sm font-semibold">{cat.label}</span>
                </div>
                <div className="grid gap-1.5">
                  {cat.questions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleSelect(q)}
                      className="flex items-center gap-2 text-left px-3 py-2 rounded-lg hover:bg-primary/5 transition-colors group text-sm"
                      data-testid={`lexicon-q-${expandedCategory}-${i}`}
                    >
                      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                      <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                        {q}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Quick tips */}
      <div className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-muted/50 text-xs text-muted-foreground">
        <Zap className="w-3.5 h-3.5 mt-0.5 text-amber-500 shrink-0" />
        <span>
          <strong className="text-foreground">Astuce :</strong> Utilisez des mots temporels comme
          <em> "hier"</em>, <em>"cette semaine"</em>, <em>"ce mois"</em> pour filtrer par date.
          Mentionnez un nom pour cibler un contact ou collaborateur.
        </span>
      </div>
    </div>
  );
}
